import React, { useState, useCallback, useEffect } from 'react';
import { API_URL } from '../api';

/* ── design tokens ── */
// Tokens ur index.css sa att rutan foljer morkt lage (produktionsgenomgangen 2026-09-28).
const T = {
  white: 'var(--kp-white, #FFF)', inset: 'var(--kp-inset, #F0F2F5)',
  border: 'var(--kp-border, #E2E8F0)', borderSubtle: 'var(--kp-border-subtle, #E4E9F0)',
  accent: 'var(--kp-accent, #3B82F6)', accentHover: 'var(--kp-accent-hover, #2563EB)',
  purple: 'var(--kp-purple, #7C3AED)', purpleBg: 'var(--kp-purple-light, #F5F3FF)',
  text: 'var(--kp-text, #1E293B)', body: 'var(--kp-text-body, #475569)', sec: 'var(--kp-text-secondary, #64748B)', muted: 'var(--kp-text-muted, #7E8FA3)',
  shadowLg: '0 8px 32px rgba(0,0,0,.15)',
};

const SESSION_KEY = 'kanplan-kanai-disclaimer-dismissed';

/**
 * KanAIDisclaimer – shown once per session before KanAI can be used.
 * EU AI Act Art. 13 – transparency: inform user they are interacting with AI.
 */
export function KanAIDisclaimer({ onAccept }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(SESSION_KEY);
    if (!dismissed) setVisible(true);
  }, []);

  const handleAccept = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setVisible(false);
    if (onAccept) onAccept();
  }, [onAccept]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: T.white, borderRadius: 14, padding: '28px 32px',
        maxWidth: 520, width: '90%', boxShadow: T.shadowLg,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 2.5l1.3 3.2 3.2 1.3-3.2 1.3L7 11.5 5.7 8.3 2.5 7l3.2-1.3L7 2.5z" />
            <path d="M17 5.5l.9 2.2 2.2.9-2.2.9L17 12.5l-.9-2.2-2.2-.9 2.2-.9.9-2.2z" />
          </svg>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
            KanAI – AI-assisterad analys
          </h2>
        </div>

        <div style={{ fontSize: 13.5, lineHeight: 1.6, color: T.body, marginBottom: 20 }}>
          <p style={{ margin: '0 0 12px' }}>
            KanAI använder artificiell intelligens (AI) för att analysera dokument, generera förslag och ge rekommendationer.
            Observera följande:
          </p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}>AI-genererat innehåll kan innehålla felaktigheter — granska alltid resultaten.</li>
            <li style={{ marginBottom: 6 }}>Dina frågor och svar loggas för kvalitetssäkring och regelefterlevnad (EU AI Act Art. 12).</li>
            <li style={{ marginBottom: 6 }}>KanAI fattar inga beslut — alla förslag kräver mänsklig bekräftelse.</li>
            <li style={{ marginBottom: 6 }}>Personuppgifter i promptar anonymiseras automatiskt i loggarna.</li>
          </ul>
          <p style={{ margin: 0, fontSize: 12, color: T.muted }}>
            Genom att fortsätta godkänner du att du har tagit del av denna information.
            Du kan alltid klicka på info-ikonen bredvid KanAI för mer detaljer.
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={handleAccept}
            style={{
              padding: '10px 24px', borderRadius: 6, border: 'none',
              background: T.purple, color: '#FFF', fontSize: 13.5, fontWeight: 600,
              cursor: 'pointer', transition: 'background 150ms',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#6D28D9'}
            onMouseOut={e => e.currentTarget.style.background = T.purple}
          >
            Jag förstår — fortsätt
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * KanAIInfoModal – "Om KanAI" modal accessible via info icon.
 * Shows transparency information per EU AI Act Art. 13.
 */
export function KanAIInfoModal({ open, onClose }) {
  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,.4)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: T.white, borderRadius: 14, padding: '28px 32px',
        maxWidth: 600, width: '92%', boxShadow: T.shadowLg, maxHeight: '80vh', overflow: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 2.5l1.3 3.2 3.2 1.3-3.2 1.3L7 11.5 5.7 8.3 2.5 7l3.2-1.3L7 2.5z" />
              <path d="M17 5.5l.9 2.2 2.2.9-2.2.9L17 12.5l-.9-2.2-2.2-.9 2.2-.9.9-2.2z" />
            </svg>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>Om KanAI</h2>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: T.muted, fontSize: 18, lineHeight: 1,
          }} title="Stäng">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ fontSize: 13.5, lineHeight: 1.7, color: T.body }}>
          <Section title="Vad är KanAI?">
            KanAI är KanPlans AI-assistent som hjälper dig att analysera dokument, generera rapporter
            och ge rekommendationer baserade på era kunskapsdokument och projektdata.
            KanAI använder externa AI-språkmodeller (Claude via AWS Bedrock i Stockholm, samt OpenAI för vissa funktioner) för textanalys.
          </Section>

          <Section title="Hur används dina data?">
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li>Dina frågor skickas till AI-tjänsten för att generera svar.</li>
              <li>Samtalshistorik sparas krypterat i din tenant-databas.</li>
              <li>Promptar loggas i audit trail med automatisk PII-anonymisering.</li>
              <li>Ingen data delas mellan olika organisationer (tenant-isolering).</li>
              <li>AI-leverantörerna (OpenAI/Anthropic) har DPA och lagrar inte KanPlan-data för träning.</li>
            </ul>
          </Section>

          <Section title="Begränsningar">
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li>KanAI kan generera felaktiga svar (hallucinationer) — verifiera alltid resultat.</li>
              <li>Svar baseras på tillgängliga kunskapsdokument; information utanför dessa kan vara ofullständig.</li>
              <li>KanAI fattar aldrig beslut autonomt — alla förslag kräver mänsklig granskning.</li>
              <li>Beräkningar och kalkyler ska alltid verifieras av ansvarig person.</li>
            </ul>
          </Section>

          <Section title="EU AI Act-efterlevnad">
            KanPlan klassificerar KanAI som ett <strong>begränsat risk-system</strong> (Art. 50).
            Följande åtgärder är implementerade:
            <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li><strong>Transparens (Art. 13):</strong> Användare informeras om att de interagerar med AI.</li>
              <li><strong>Loggning (Art. 12):</strong> Alla interaktioner loggas automatiskt med 24 månaders retention.</li>
              <li><strong>Mänsklig tillsyn (Art. 14):</strong> AI-förslag kräver explicit godkännande.</li>
              <li><strong>Riskhantering (Art. 9):</strong> Regelbunden testning av hallucination och bias.</li>
            </ul>
          </Section>

          <Section title="Kontakt">
            Frågor om KanAI och databehandling: kontakta er systemadministratör
            eller KanPlans dataskyddsombud.
          </Section>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px', borderRadius: 6, border: `1px solid ${T.border}`,
              background: T.white, color: T.body, fontSize: 13.5, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Stäng
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * KanAIInfoButton – small info icon button that opens the info modal.
 */
export function KanAIInfoButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Om KanAI"
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 3,
          color: T.muted, display: 'inline-flex', alignItems: 'center',
          transition: 'color 150ms',
        }}
        onMouseOver={e => e.currentTarget.style.color = T.purple}
        onMouseOut={e => e.currentTarget.style.color = T.muted}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </button>
      <KanAIInfoModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: T.text }}>{title}</h3>
      <div>{children}</div>
    </div>
  );
}
